// TODO: this FILE IS DUPLICATED, the injection is happening 2x for now please keep both /frontend/classic/_lib/deputy-embed
window.__APP__ = window.__APP__ || {};
window.__APP__.EMBED = window.__APP__.EMBED || {};
window.__APP__.EMBED = {
  ...window.__APP__.EMBED,
  /**
   * @param {Window|Window|WindowProxy} localWindow
   * @return {Boolean}
   */
  isDeputyEmbedded(localWindow: Window = window): boolean {
    let isInEmbed;

    try {
      isInEmbed = !localWindow?.top?.__APP__;
    } catch (e) {
      if (e instanceof Error && e.name === 'SecurityError') {
        isInEmbed = true;
      } else {
        console.error(e);
        isInEmbed = false;
      }
    }

    return isInEmbed;
  },
  /**
   * @param {Window|Window|WindowProxy} win
   * @param {Window|Window|WindowProxy} testWindow // added testWindow for testing purposes
   * @return {Window|Window|WindowProxy|undefined}
   */
  getRootDeputyFrame(win?: Window, testWindow?: Window): Window | undefined {
    const localWindow = testWindow || window;

    // first pass check if it's embedded
    if (!win && !window.__APP__.EMBED.isDeputyEmbedded(localWindow)) {
      return localWindow.top || undefined;
    } else if (!win) {
      win = localWindow;
    }

    let returnWindow; // this is required because returning win here will be undefined

    try {
      if (!win.parent.__APP__) {
        returnWindow = win;
      } else {
        return window.__APP__.EMBED.getRootDeputyFrame(win.parent);
      }
    } catch (e) {
      if (e instanceof Error && e.name === 'SecurityError') {
        // this is required because returning win here will be undefined
        returnWindow = win;
      } else {
        console.error(e);
      }
    }

    if (returnWindow) {
      return returnWindow;
    }
  },
  /**
   * @return {Boolean}
   * @param {Window} win
   */
  isWindowDeputyRootFrame(win: Window = window): boolean {
    if (!window.__APP__.EMBED.isDeputyEmbedded()) {
      return win.self === win.top;
    }

    return window.__APP__.EMBED.getRootDeputyFrame(win) === win.self;
  },
  /**
   * @return {Boolean}
   * @param {String} module
   * @param {String} component
   * @param {Boolean} enforcePermissionsCheck If true, permissions will be checked even if Deputy is not in embed mode. Defaults to false, and always has access if Deputy is not in embed mode.
   */
  hasAccess(
    module: string,
    component: string,
    enforcePermissionsCheck: boolean = false
  ): boolean {
    // Since enforcePermissionsCheck defaults to false,
    // existing function calls that do not pass enforcePermissionsCheck
    // will continue to work as before without any changes.
    if (!enforcePermissionsCheck) {
      return !(
        window.__APP__.EMBED.isDeputyEmbedded() &&
        window.__APP__.EMBED.PERMISSIONS.modules[module]?.deny?.includes(
          component
        )
      );
    }

    // If enforcePermissionsCheck is true, permissions will be checked even if Deputy is not in embed mode.
    if (!window.__APP__.EMBED.PERMISSIONS) {
      // This scenario is not expected to occur at runtime.
      // However, if it does, we return !__APP__.EMBED.isDeputyEmbedded()
      // to preserve the existing behavior and ensure consistent handling.
      return !window.__APP__.EMBED.isDeputyEmbedded();
    }

    // First, check if the component is explicitly listed in the allowed components.
    // If not explicitly allowed, verify that the component is not listed in the denied components.
    return (
      window.__APP__.EMBED.PERMISSIONS.modules[module]?.allow?.includes(
        component
      ) ||
      !window.__APP__.EMBED.PERMISSIONS.modules[module]?.deny?.includes(
        component
      )
    );
  },
  /**
   * @return {Boolean}
   * @param {String} module
   * @param {String} component
   * @param {Boolean} enforcePermissionsCheck If true, permissions will be checked even if Deputy is not in embed mode. Defaults to false, and always not disable if Deputy is not in embed mode.
   */
  isDisabled(
    module: string,
    component: string,
    enforcePermissionsCheck?: boolean
  ): boolean {
    // If enforcePermissionsCheck is false and Deputy is not in embed mode,
    // return false to indicate that the component is always enabled in DIRECT mode.
    if (!enforcePermissionsCheck && !window.__APP__.EMBED.isDeputyEmbedded()) {
      return false;
    }

    // If enforcePermissionsCheck is true, permissions will be checked even if Deputy is not in embed mode.
    // Return true if the component is listed in the disabled components, meaning it is disabled in UI.
    // Otherwise, return false to indicate that the component is enabled in UI.
    return !!window.__APP__.EMBED.PERMISSIONS.modules[
      module
    ]?.disable?.includes(component);
  },
};

interface NavigateData {
  route?: string;
}

interface ToggleModalData {
  id?: string;
  action?: string;
}

/**
 * @param {Object} data
 * @param {string} data.route
 */
function navigate({ route = '#me' }: NavigateData = {}): void {
  const rootFrame = window.__APP__?.EMBED?.getRootDeputyFrame();
  if (rootFrame?.__APP__?.ROUTER) {
    rootFrame.__APP__.ROUTER.push(route);
  }
}

function toggleModal({ id = '', action = '' }: ToggleModalData = {}): void {
  const legacyModals = ['#modal-location-install-config'];
  if (legacyModals.includes(id)) {
    window.__APP__.EMBED.getRootDeputyFrame().$(id).modal(action);
  }
}

interface MessageEventData {
  type: string;
  id?: string;
  route?: string;
}

const messageTypeToFunction: Record<string, (data: MessageEventData) => void> =
  {
    'partner-navigation': navigate,
    'toggle-modal': toggleModal,
  };

if (window.__APP__?.EMBED?.isDeputyEmbedded()) {
  window.onmessage = function (
    e: MessageEvent = { data: { type: '' } } as MessageEvent
  ): void {
    const onMessageHandler = messageTypeToFunction[e.data.type as string];
    if (onMessageHandler) {
      // temporary while migrating to new business settings
      // use router instead of toggling modal
      if (
        e.data.type === 'toggle-modal' &&
        (e.data as MessageEventData).id === '#modal-location-install-config' &&
        window.$dt?.featureFlags?.check('mbd-293-business-settings-v2')
      ) {
        navigate({ route: '#business-settings' });
      } else {
        onMessageHandler(e.data as MessageEventData);
      }
    }
  };
}

export const getRootDeputyFrame = window.__APP__.EMBED.getRootDeputyFrame;
export const isDeputyEmbedded = window.__APP__.EMBED.isDeputyEmbedded;
export const isWindowDeputyRootFrame =
  window.__APP__.EMBED.isWindowDeputyRootFrame;
export const hasAccess = window.__APP__.EMBED.hasAccess;

if (isDeputyEmbedded()) {
  import('@webapp-next/assets/css/deputy-embed.css');
}
